import React from "react";
import { Box, Stack } from "@mui/material";
import CustomLayout from "../layout";
import CustomAlert from "../custom-alert";
import PrimaryButton from "../buttons/primary-button";
import CommanErrorModal from "../modal/common-error-modal";
import { useErrorMessages } from "../../utils/error-messages.constants";

// Fallback shown instead of a blank screen when a page throws while rendering.
// CommanErrorModal logs the error code to CloudWatch RUM when it opens.
const PageErrorFallback = ({ errorKey, errorText, onRetry }) => {
	const { getErrorMessage } = useErrorMessages();
	const [modalOpen, setModalOpen] = React.useState(true);
	const errorData = getErrorMessage(errorKey);

	return (
		<CustomLayout>
			<CommanErrorModal
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
				title={errorData?.title}
				message={errorData?.message}
				errorCode={errorData?.code}
				errorResponse={errorText}
			/>
			<Box p={3}>
				<CustomAlert
					subject={errorData?.title || "Something went wrong"}
					details={errorData?.message}
					severity="error"
				/>
				<Stack direction="row" mt={2}>
					<PrimaryButton text="Try again" onClick={onRetry} />
				</Stack>
			</Box>
		</CustomLayout>
	);
};

class PageErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
		this.handleRetry = this.handleRetry.bind(this);
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, info) {
		// Keep the stack in the browser console so the failing component can be found.
		// eslint-disable-next-line no-console
		console.error("Page render error:", error, info?.componentStack);
	}

	handleRetry() {
		this.setState({ error: null });
	}

	render() {
		const { error } = this.state;
		const { children, errorKey = "PAGE_RENDER_ERROR" } = this.props;
		if (error) {
			return (
				<PageErrorFallback
					errorKey={errorKey}
					errorText={String(error?.message || error)}
					onRetry={this.handleRetry}
				/>
			);
		}
		return children;
	}
}

export default PageErrorBoundary;
